import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, apiError, humanize } from "@/lib/api";
import { useWorkspace } from "@/context/WorkspaceContext";
import { Field, ListInput } from "@/components/common";

export function useCrud(resource) {
  const { path, wid } = useWorkspace();
  const qc = useQueryClient();
  const done = (msg) => { qc.invalidateQueries({ queryKey: ["ws", wid] }); if (msg) toast.success(msg); };
  const wrap = (fn, msg) => async (...a) => { try { const r = await fn(...a); done(msg); return r.data; } catch (e) { toast.error(apiError(e)); throw e; } };
  return {
    create: wrap((body) => api.post(path(`/r/${resource}`), body), "Created"),
    update: wrap((id, body) => api.patch(path(`/r/${resource}/${id}`), body), "Saved"),
    remove: wrap((id) => api.delete(path(`/r/${resource}/${id}`)), "Archived"),
  };
}

function Input({ f, value, onChange }) {
  const tid = `record-field-${f.k}`;
  if (f.type === "textarea") return <textarea data-testid={tid} rows={f.rows || 5} className="cq-input" value={value || ""} onChange={(e) => onChange(e.target.value)} />;
  if (f.type === "list") return <ListInput testId={tid} value={value || []} onChange={onChange} />;
  if (f.type === "bool") return (
    <button type="button" data-testid={tid} onClick={() => onChange(!value)} className={`cq-btn ${value ? "cq-btn-success" : "cq-btn-ghost"} !py-1.5`}>{value ? "Yes" : "No"}</button>);
  if (f.type === "select") return (
    <select data-testid={tid} className="cq-input" value={value ?? ""} onChange={(e) => onChange(e.target.value || null)}>
      {f.allowEmpty && <option value="">—</option>}
      {value && !f.options.some((o) => (Array.isArray(o) ? o[0] : o) === value) && <option value={value} disabled>{humanize(value)} (set via Approvals)</option>}
      {f.options.map((o) => { const [v, l] = Array.isArray(o) ? o : [o, o]; return <option key={v} value={v}>{l}</option>; })}
    </select>);
  return <input data-testid={tid} type={f.type || "text"} className="cq-input" value={value || ""} onChange={(e) => onChange(e.target.value)} />;
}

export default function RecordDialog({ open, onOpenChange, title, fields, initial, onSubmit, submitLabel = "Save" }) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm(initial || {}); }, [open, initial]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = {};
    fields.forEach((f) => { if (form[f.k] !== undefined && JSON.stringify(form[f.k]) !== JSON.stringify(initial?.[f.k])) body[f.k] = f.type === "list" ? (form[f.k] || []).map((s) => s.trim()).filter(Boolean) : form[f.k]; });
    try { await onSubmit(body); onOpenChange(false); } catch { /* toast shown by caller */ } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="record-dialog" className="bg-[#0A1024] border-[#243153] text-[#F7FAFF] max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">{title}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
          {fields.map((f) => (
            <div key={f.k} className={f.half ? "" : "sm:col-span-2"}>
              <Field label={f.label} hint={f.hint}><Input f={f} value={form[f.k]} onChange={(v) => setForm((s) => ({ ...s, [f.k]: v }))} /></Field>
            </div>
          ))}
          <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
            <button type="button" data-testid="record-dialog-cancel" onClick={() => onOpenChange(false)} className="cq-btn cq-btn-ghost">Cancel</button>
            <button data-testid="record-dialog-submit" disabled={busy} className="cq-btn cq-btn-primary">{busy && <Loader2 size={14} className="animate-spin" />} {submitLabel}</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
